import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Button, Select, Spin, Alert, Popconfirm } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconSpeakerphone } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminAnnouncements,
  sendAdminAnnouncement,
  IAdminAnnouncement,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Simple form to send a notice to everyone, or to a segment (all hosts, all
// guests).
const AnnouncementsSection: React.FC = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<IAdminAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('All');
  const [sending, setSending] = useState(false);

  const loadHistory = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminAnnouncements()
      .then(setHistory)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      message.error(translate(t, TRANSLATIONS.ADMIN_REASON_REQUIRED));
      return;
    }
    setSending(true);
    try {
      await sendAdminAnnouncement(title.trim(), body.trim(), segment);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setTitle('');
      setBody('');
      loadHistory();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_ANNOUNCEMENTS)}
      </Title>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480, marginBottom: 32 }}>
        <div>
          <Text>{translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_TITLE_LABEL)}</Text>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>
        <div>
          <Text>{translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_BODY_LABEL)}</Text>
          <TextArea rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} />
        </div>
        <div>
          <Text>{translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_LABEL)}</Text>
          <Select
            value={segment}
            onChange={setSegment}
            style={{ width: '100%' }}
            options={[
              { value: 'All', label: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_ALL) },
              { value: 'Hosts', label: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_HOSTS) },
            ]}
          />
        </div>
        <Popconfirm
          title={translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEND_CONFIRM)}
          onConfirm={handleSend}
          disabled={!title.trim() || !body.trim()}
        >
          <Button type="primary" loading={sending} disabled={!title.trim() || !body.trim()}>
            {translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEND_BUTTON)}
          </Button>
        </Popconfirm>
      </div>

      <Title level={5} style={{ marginBottom: 12 }}>
        {translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_HISTORY_TITLE)}
      </Title>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_HISTORY_LOAD_ERROR)} />
      ) : history.length === 0 ? (
        <EmptyState
          icon={<IconSpeakerphone size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_HISTORY_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_HISTORY_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminAnnouncement>
          rowKey="id"
          dataSource={history}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.title,
            subtitle: translate(
              t,
              record.targetSegment === 'Hosts'
                ? TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_HOSTS
                : TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_ALL,
            ),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_RECIPIENTS), value: record.recipientCount },
              { label: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_DATE), value: new Date(record.createdAt).toLocaleDateString() },
              { label: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_SENT_BY), value: record.sentByAdminName, wide: true },
            ],
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_TITLE),
              dataIndex: 'title',
              key: 'title',
              sorter: textSorter<IAdminAnnouncement>('title'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_SEGMENT),
              dataIndex: 'targetSegment',
              key: 'targetSegment',
              filters: enumColumnFilters(['All', 'Hosts'], (value) =>
                translate(t, value === 'Hosts' ? TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_HOSTS : TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_ALL)
              ),
              onFilter: (value, record: IAdminAnnouncement) => record.targetSegment === value,
              render: (value: string) =>
                value === 'Hosts'
                  ? translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_HOSTS)
                  : translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_SEGMENT_ALL),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_RECIPIENTS),
              dataIndex: 'recipientCount',
              key: 'recipientCount',
              sorter: numberSorter<IAdminAnnouncement>('recipientCount'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_SENT_BY),
              dataIndex: 'sentByAdminName',
              key: 'sentByAdminName',
              sorter: textSorter<IAdminAnnouncement>('sentByAdminName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ANNOUNCEMENTS_COL_DATE),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminAnnouncement>('createdAt'),
              defaultSortOrder: 'descend',
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
          ]}
        />
      )}
    </div>
  );
};

export default AnnouncementsSection;
